import { take, switchMap, Observable, of } from 'rxjs';
import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import cloneDeep from 'lodash.clonedeep';

import { DataType, ProjectSetup, SelectionItem } from '@shared/models';
import { DprActivity } from '@shared/models/dpr';
import { DprActivityCustomDataGridData } from '@dpr-details/models/grid';
import { UserDetailsFacade } from '@shared/state/facades';
import {
  compareTime,
  convertTimeToDate,
  getValue,
  buildBooleanValues,
} from '@shared/utils';
import { InlineCustomDataColumnsPipe } from '@report/pipes';
import { DprActivityModelReport, DprActivityReport } from '@report/models';
import { ReportOrderItemSectionType } from '@shared/enums';
import { CalculatedCustomDataPipe, CustomDataFormulaPipe } from '@shared/pipes';

@Injectable()
export class ReportActivitiesService {
  constructor(
    private userDetailsFacade: UserDetailsFacade,
    private calculatedCustomDataPipe: CalculatedCustomDataPipe,
    private customDataFormulaPipe: CustomDataFormulaPipe,
    private inlineCustomDataColumnsPipe: InlineCustomDataColumnsPipe,
    private datePipe: DatePipe
  ) {}

  constructActivitiesModel(
    activitiesList: DprActivity[]
  ): Observable<DprActivityModelReport[]> {
    return this.userDetailsFacade.query.projectSetup$.pipe(
      take(1),
      switchMap((projectSetup) => {
        const activitiesModel: DprActivityModelReport[] = [];
        const activitiesReport = this.activitiesReport(
          activitiesList,
          projectSetup
        );
        const useDefaultOrder =
          projectSetup.reportConfiguration.reportOrder.useDefaultOrder;

        projectSetup.activityStreams.forEach((activityStream) => {
          let activities = activitiesReport.filter(
            (a) =>
              a.parallelActivityType === activityStream.parallelActivityType
          );

          let inlineCustomData = this.inlineCustomDataColumnsPipe.transform(
            activityStream,
            projectSetup.activityStreamInlineCustomDataList,
            projectSetup.customDataList
          );

          if (!useDefaultOrder) {
            const reportItemCustomDataAssignation =
              projectSetup.reportConfiguration.reportOrder.order.find(
                (x) =>
                  x.reportOrderItemSectionType ===
                    ReportOrderItemSectionType.ActivityStream &&
                  x.identifier === activityStream.parallelActivityType
              )?.customDataAssignations;

            const customDataIdsNotIncludedInPdf =
              reportItemCustomDataAssignation
                .filter((x) => !x.includeInPDF)
                .map((x) => x.customDataId);

            activities = activities.map((x) => ({
              ...x,
              customDataReport: x.customDataReport.filter((y) =>
                customDataIdsNotIncludedInPdf.every((z) => y.customDataId !== z)
              ),
            }));

            inlineCustomData = inlineCustomData.filter((y) =>
              customDataIdsNotIncludedInPdf.every((z) => y.id !== z)
            );
          }

          if (activities.length) {
            activitiesModel.push({
              title: activityStream.value,
              activities,
              inlineCustomData,
              parallelActivityType: activityStream.parallelActivityType,
            });
          }
        });

        return of(activitiesModel);
      })
    );
  }

  private activitiesReport(
    activitiesList: DprActivity[],
    projectSetup: ProjectSetup
  ): DprActivityReport[] {
    const activitiesReport: DprActivityReport[] = [];

    // sort by start, then finish
    const sortedActivities: DprActivity[] = cloneDeep(activitiesList).sort(
      (a, b) =>
        compareTime(convertTimeToDate(a.start), convertTimeToDate(b.start)) ||
        compareTime(convertTimeToDate(a.finish), convertTimeToDate(b.finish))
    );

    sortedActivities.forEach((activity) => {
      const toAdd: DprActivityReport = {
        activityGroupName: (projectSetup.activityGroupList ?? []).find(
          (x) => x.id === activity.activityGroupId
        ).name,
        activityName: (projectSetup.activityList ?? []).find(
          (x) => x.id === activity.activityId
        ).name,
        locationName: (projectSetup.locationList ?? []).find(
          (x) => x.id === activity.locationId
        ).name,
        start: activity.start,
        finish: activity.finish,
        parallelActivityType: activity.parallelActivityType ?? 0,
        comment: activity.comment || '-',
        customDataReport: [],
        subActivitiesReport: [],
        calculatedCustomDataValueWithDuration: '',
        customDataFormulaValue: '',
      };

      const dprActivityCustomDataList = activity.parallelActivityType
        ? activity.dprParallelActivityCustomDataList
        : activity.dprActivityCustomDataList;

      const dprActivityCustomDataGridData: DprActivityCustomDataGridData[] =
        dprActivityCustomDataList.map((cd) => ({
          ...cd,
          name: projectSetup.customDataList.find(
            (x) => x.id === cd.customDataId
          )?.name,
          dataTypeId: projectSetup.customDataList.find(
            (x) => x.id === cd.customDataId
          )?.dataTypeId,
          decimalCount: projectSetup.customDataList.find(
            (x) => x.id === cd.customDataId
          )?.decimalCount,
        }));

      // Calculated custom data

      if (projectSetup.subprojectConfiguration.enableCalculatedCustomData) {
        toAdd.calculatedCustomDataValueWithDuration =
          this.calculatedCustomDataPipe.transform(
            projectSetup.subprojectConfiguration
              .calculatedCustomDataConfiguration.customDataId,
            dprActivityCustomDataGridData,
            activity.start,
            activity.finish,
            activity.id
          ) || '-';
      }

      // Custom data formula

      if (
        projectSetup.subprojectConfiguration.enableCustomDataFormula &&
        projectSetup.subprojectConfiguration.customDataFormulaConfiguration
      ) {
        toAdd.customDataFormulaValue =
          this.customDataFormulaPipe.transform(
            projectSetup.subprojectConfiguration.customDataFormulaConfiguration,
            dprActivityCustomDataGridData,
            activity.id,
            projectSetup.customDataList
          ) || '-';
      }

      // Custom data

      const activityMobilisationCustomDataIds: number[] =
        projectSetup.activityMobilisationList
          .filter((am) => am.activityId === activity.activityId)
          .map((am) => am.customDataId);

      dprActivityCustomDataList.forEach((cd) => {
        if (cd && activityMobilisationCustomDataIds.includes(cd.customDataId)) {
          const customData = (projectSetup.customDataList ?? []).find(
            (x) => x.id === cd.customDataId
          );

          let listOptions: SelectionItem[];

          switch (customData.dataTypeId) {
            case DataType.List:
              listOptions = cloneDeep(
                projectSetup.customDataValueList || []
              ).map((x) => ({ id: x.id, name: x.value }));
              break;
            case DataType.Boolean:
              listOptions = buildBooleanValues(
                customData.booleanTerminologyId
              ).map((x) => ({ id: x.id, name: x.value }));
              break;
            default:
              listOptions = [];
          }

          if (!!cd.value?.toString()) {
            const value = getValue(
              cd.value,
              customData.dataTypeId,
              listOptions,
              this.datePipe,
              customData.decimalCount
            );

            toAdd.customDataReport.push({
              customDataId: cd.customDataId,
              name: customData.name,
              comment: cd.comment ?? '-',
              value,
              priority: customData.priority,
            });
          }
        }
      });

      toAdd.customDataReport?.sort((a, b) => a.priority - b.priority);

      // Subactivities

      const dprSubActivities = activity.parallelActivityType
        ? activity.dprParallelSubActivityList
        : activity.dprSubActivities;

      dprSubActivities.forEach((subActivity) => {
        toAdd.subActivitiesReport.push({
          name: subActivity.name,
          start: subActivity.start,
          finish: subActivity.finish,
        });
      });

      activitiesReport.push(toAdd);
    });

    return activitiesReport;
  }
}
